import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Anchor } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const empty = { name: "", type: "Pelabuhan", city: "", contact: "" };
const TYPES = ["Pelabuhan", "Supplier", "Nelayan", "Tambak", "Lainnya"];

export default function MasterOrigins() {
  const { can } = useAuth(); const isAdmin = can("master_manage");
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/origins").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { name, type, city, contact } = form.data;
    try {
      if (form.item) await api.put(`/origins/${form.item.id}`, { name, type, city, contact }); else await api.post("/origins", { name, type, city, contact });
      toast.success("Asal ikan disimpan"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Hapus asal ${o.name}?`)) return;
    try { await api.delete(`/origins/${o.id}`); toast.success("Asal ikan dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="origins-page">
      <PageHeader title="Master Asal Ikan" subtitle="Pelabuhan, supplier, dan sumber ikan yang masuk ke cold storage">
        {isAdmin && <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm({ item: null, data: empty })} data-testid="btn-add-origin"><Plus className="h-4 w-4 mr-1" /> Tambah Asal</Button>}
      </PageHeader>

      <div className="card-aqua overflow-hidden rise rise-1">
        <table className="w-full text-sm" data-testid="table-origins">
          <thead className="table-head"><tr><th className="text-left px-4 py-3">Nama</th><th className="text-left px-4 py-3">Tipe</th><th className="text-left px-4 py-3">Kota / Daerah</th><th className="text-left px-4 py-3">Kontak</th><th className="px-4 py-3 text-right">Aksi</th></tr></thead>
          <tbody>
            {list.map((o, idx) => (
              <tr key={o.id} className="border-t border-sky-50 hover:bg-sky-50/50" data-testid={`origin-row-${idx}`}>
                <td className="px-4 py-3 font-semibold text-slate-800 flex items-center gap-2"><Anchor className="h-4 w-4 text-sky-600" />{o.name}</td>
                <td className="px-4 py-3"><span className="px-2.5 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-xs font-semibold text-sky-800">{o.type}</span></td>
                <td className="px-4 py-3 text-slate-600">{o.city || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{o.contact || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {isAdmin && (
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => setForm({ item: o, data: { ...empty, ...o } })} data-testid={`btn-edit-origin-${idx}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(o)} data-testid={`btn-delete-origin-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="origin-form-dialog">
          <DialogHeader><DialogTitle>{form?.item ? "Edit Asal Ikan" : "Tambah Asal Ikan"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama</Label><Input value={form.data.name} onChange={(e) => set("name", e.target.value)} required placeholder="cth. Pelabuhan Bitung" data-testid="input-origin-name" /></div>
              <div className="space-y-1.5">
                <Label>Tipe</Label>
                <Select value={form.data.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="select-origin-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Kota / Daerah</Label><Input value={form.data.city} onChange={(e) => set("city", e.target.value)} data-testid="input-origin-city" /></div>
              <div className="space-y-1.5"><Label>Kontak</Label><Input value={form.data.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Opsional" data-testid="input-origin-contact" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-origin-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
